import React, { useState, useEffect, useRef } from "react";
import { utcToLocalDateTime, utcToDate } from "../../components/helper";
import { useTranslation } from "../../contexts/TranslationContext";
import "./AllProfiles.scss";
import {
	unBlockUser,
	getBlockUsers,
	getPhotos,
	getSelfie,
} from "../../api/apiServiceFetch";
import PhotoModal from "../../components/PhotoModal";
import { getFileBaseUrl } from "../../config/environment";
import { useAuth } from "../../contexts/auth";

const ITEMS_PER_PAGE = 30;
const BlockedProfiles = () => {
	const { userBase } = useAuth();
	const { t } = useTranslation();
	const [users, setUsers] = useState([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [expandedUserId, setExpandedUserId] = useState(null);
	const [userPhotos, setUserPhotos] = useState([]);
	const [userSelfie, setUserSelfie] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [userPhoto, setUserPhoto] = useState("");
	const filePath = getFileBaseUrl();

	const [filterTerm, setFilterTerm] = useState("");
	const [totalPages, setTotalPages] = useState(1);
	const [filterCountry, setFilterCountry] = useState("");
	const [filterCity, setFilterCity] = useState("");
	const [filterSex, setFilterSex] = useState(0);
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const modalRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [showBlock, setShowBlock] = useState(false);
	const fetchIdRef = useRef(0);

	const fetchUsers = async () => {
		const fetchId = ++fetchIdRef.current;
		try {
			const users = await getBlockUsers(
				pageNumber,
				ITEMS_PER_PAGE,
				filterTerm,
				filterCountry,
				filterCity,
				filterSex,
			);

			if (fetchId !== fetchIdRef.current) return; // stale response — a newer fetch owns the list

			if (users.data.length === 0) {
				setUsers([]);
				setTotalPages(1);
				if (pageNumber > 1) setPageNumber(1); // page went out of range — back to the first page
			} else {
				setUsers(users.data);
				const totalPages = Math.ceil(
					users.data[0].totalRecords / ITEMS_PER_PAGE,
				);
				setTotalPages(totalPages < 1 ? 1 : totalPages);
			}
		} catch (error) {
			if (fetchId !== fetchIdRef.current) return;
			setUsers([]);
			console.error("Error fetching users by IDs:", error);
		} finally {
			if (fetchId === fetchIdRef.current) setLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, [pageNumber, filterTerm, filterCountry, filterCity, filterSex]);

	const fetchUserPhotos = async (userId) => {
		try {
			const resp = await getPhotos(userId);
			setUserPhotos(Array.isArray(resp) ? resp : []);
		} catch (error) {
			console.error("Failed to fetch photos:", error);
			setUserPhotos([]);
		}
	};

	const fetchUserSelfie = async (userId) => {
		try {
			const resp = await getSelfie(userId);
			// Empty object => no selfie; guard on fileName so we never build
			// .../Selfie/{id}/undefined.
			setUserSelfie(resp && resp.fileName ? resp : null);
		} catch (error) {
			console.error("Failed to fetch selfie:", error);
			setUserSelfie(null);
		}
	};

	const handleViewPhoto = (src) => {
		setUserPhoto(src);
		setShowModal(true);
	};

	const handleExpandClick = (userId) => {
		if (expandedUserId === userId) {
			setExpandedUserId(null);
			setUserPhotos([]);
			setUserSelfie(null);
			return;
		}
		setExpandedUserId(userId);
		fetchUserPhotos(userId);
		fetchUserSelfie(userId);
	};
	const toggleFilterModal = (type) => {
		setIsFilterModalOpen(type);
	};
	const handleFilterRemove = () => {
		setFilterCountry("");
		setFilterCity("");
		setFilterSex(0); // Reset the sex filter (to an invalid value)
		setPageNumber(1);
	};

	const applyFiltersHandler = () => {
		setPageNumber(1);
		toggleFilterModal(false);
	};
	const handleClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsFilterModalOpen(false);
		}
	};

	useEffect(() => {
		if (isFilterModalOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isFilterModalOpen]);

	if (loading) {
		return <div>Загрузка...</div>;
	}

	const applyBlock = async () => {
		await unBlockUser(expandedUserId);
		setUsers((prevUsers) =>
			prevUsers.filter((user) => user.id !== expandedUserId),
		);
		setShowBlock(false);
	};
	const handlePreviousPage = () => {
		if (pageNumber > 1) {
			setPageNumber((prev) => prev - 1);
		}
	};
	const toggleBlockModal = () => {
		setShowBlock(!showBlock);
	};
	const handleNextPage = () => {
		if (pageNumber < totalPages) {
			setPageNumber((prev) => prev + 1);
		}
	};
	const handleSearchChange = (e) => {
		setFilterTerm(e.target.value);
		setPageNumber(1); // Reset to first page when search term changes
	};

	return (
		<div className="AllProfiles">
			{showModal && (
				<PhotoModal photo={userPhoto} onClose={() => setShowModal(false)} />
			)}
			{showBlock && (
				<div className="filter-modal">
					<div className="filter-modal-content">
						<h3>Разблокировать пользователя?</h3>
						<button onClick={applyBlock}>Разблокировать</button>
						<button onClick={toggleBlockModal}>Отмена</button>
					</div>
				</div>
			)}
			<div className="filter-button">
				<button onClick={() => toggleFilterModal(true)}>Фильтровать</button>
				<button onClick={handleFilterRemove}>Удалить фильтры</button>
			</div>
			<div className="search-bar">
				<input
					type="text"
					placeholder="Поиск: id, tg, почта, имя, никнейм"
					value={filterTerm}
					onChange={handleSearchChange}
					className="search-input"
				/>
			</div>
			{isFilterModalOpen && (
				<div className="filter-modal">
					<div className="filter-modal-content" ref={modalRef}>
						<h3>Фильтр</h3>
						<input
							type="text"
							placeholder="Страна"
							value={filterCountry}
							onChange={(e) => setFilterCountry(e.target.value)}
							className="filter-input"
						/>
						<input
							type="text"
							placeholder="Город"
							value={filterCity}
							onChange={(e) => setFilterCity(e.target.value)}
							className="filter-input"
						/>
						<select
							value={filterSex}
							onChange={(e) => setFilterSex(Number(e.target.value))}
							className="filter-input"
						>
							<option value={0}>Фильтр по полу</option>
							<option value={1}>Мужчина</option>
							<option value={2}>Женщина</option>
						</select>
						<button onClick={applyFiltersHandler}>Приминить</button>
						<button onClick={() => toggleFilterModal(false)}>Отмена</button>
					</div>
				</div>
			)}
			{users.length == 0 ? <div>Нет профилей</div> : ""}
			{users.map((user) => (
				<div key={user.id} className="profile-card">
					<div
						className="profile-summary"
						onClick={() => handleExpandClick(user.id)}
						style={{
							backgroundColor: user.sex === 1 ? "#e6f0ff" : "#fff0f6", // Light blue or light pink
							borderRadius: "5px", // Optional: softens edges
						}}
					>
						<div className="profile-summary-cont">
							<h2>
								{user.fullName} (id: {user.id}{user.legacyUserId ? `, tg: ${user.legacyUserId}` : ''})
							</h2>
							<p> Никнейм: {user.userName}</p>
							<p> Почта: {user.mail || "—"}</p>
							<p> Пол: {user.sex === 1 ? "Мужчина" : "Женщина"}</p>
						</div>
					</div>
					{expandedUserId === user.id && (
						<div className="profile-details">
							<div className="details-info">
								<div className="info-text">
									<p>
										Дата рождения: {utcToDate(user.birthday)} (Возраст:{" "}
										{user.age})
									</p>
									<p>Страна: {user.country}</p>
									<p>Город: {user.city}</p>
									{user.wRole && <p>Роль: {t(user.wRole)}</p>}
									{user.profession && <p>Дейтельность: {t(user.profession)}</p>}
									<p>О себе: {user.description}</p>
									<p>Цель знакомства: {t(user.propose)}</p>
									<p>Причина блока: {user.reason}</p>
									<p>Дата Блокировки: {utcToLocalDateTime(user.blockDate)}</p>
									<p>Дата Регистрации: {utcToLocalDateTime(user.cDate)}</p>
									<p>
										Дата Последнего входа:{" "}
										{utcToLocalDateTime(user.lastLoginDate)}
									</p>
									{(user.cntReg > 1 || user.cntDel > 0) && (
										<p>
											{user.cntReg > 1 && `Кол. регистраций: ${user.cntReg} `}
											{user.cntDel > 0 &&
												`${user.sex === 2 ? "Удалилась" : "Удалился"}: ${user.cntDel}`}
										</p>
									)}
								</div>
							</div>

							<div className="photos">
								{userSelfie?.fileName ? (
									<div className="photoslot">
										<div className="photoslot-inner">
											<img
												style={{ border: "5px solid red" }}
												src={`${filePath}/Selfie/${user?.id}/${userSelfie.fileName}`}
												onClick={() =>
													handleViewPhoto(
														`${filePath}/Selfie/${user?.id}/${userSelfie.fileName}`,
													)
												}
												alt="Selfie"
											/>
										</div>
									</div>
								) : (
									<div>
										<h1 style={{ color: "red" }}>Нет селфи</h1>
									</div>
								)}
								{userPhotos.length > 0 ? (
									userPhotos.map((photo, index) => (
										<div key={photo.id} className="photoslot">
											<div
												className="photoslot-inner"
												style={{ position: "relative" }}
											>
												<div
													title={
														typeof photo.matchPercent === "number"
															? `Совпадение с селфи: ${photo.matchPercent.toFixed(0)}%`
															: "Лицо не сверено"
													}
													style={{
														position: "absolute",
														top: 4,
														left: 4,
														padding: "2px 6px",
														borderRadius: 4,
														background:
															typeof photo.matchPercent !== "number"
																? "#777"
																: photo.matchPercent >= 50
																	? "#2e7d32"
																	: photo.matchPercent >= 30
																		? "#ed6c02"
																		: "#c62828",
														color: "white",
														fontSize: 12,
														fontWeight: 600,
														zIndex: 1,
													}}
												>
													{typeof photo.matchPercent === "number"
														? `${photo.matchPercent.toFixed(0)}%`
														: "?"}
												</div>
												<img
													src={`${filePath}/Photo/${user?.id}/${photo.fileName}`}
													onClick={() =>
														handleViewPhoto(
															`${filePath}/Photo/${user?.id}/${photo.fileName}`,
														)
													}
													alt={`Photo ${index + 1}`}
												/>
											</div>
										</div>
									))
								) : (
									<div>
										<h1 style={{ color: "red" }}>Нет фоток</h1>
									</div>
								)}
							</div>

							<div className="actions">
								<button
									style={{ background: "green" }}
									onClick={() => setShowBlock(true)}
								>
									Разблокировать
								</button>
							</div>
						</div>
					)}
				</div>
			))}
			<div className="pagination">
				<button onClick={handlePreviousPage} disabled={pageNumber === 1}>
					Назад
				</button>
				<span>
					Страница {pageNumber} из {totalPages}
				</span>
				<button onClick={handleNextPage} disabled={pageNumber === totalPages}>
					Дальше
				</button>
			</div>
		</div>
	);
};

export default BlockedProfiles;
